import { z } from 'zod'

import { ACCESS_LEVELS, type AccessLevel } from '#admin/features/platform/tenants/api.ts'
import { api } from '#admin/lib/api-client.ts'

/**
 * Plans — what a tenant bought: which modules at which level, and the quota ceilings (Q-1).
 * Editing one reaches every edition on it, so every write here needs `platform:plans.manage`.
 */

const levels = z.record(z.string(), z.enum(ACCESS_LEVELS))

export const planSummarySchema = z.object({
  id: z.uuid(),
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  quotas: z.record(z.string(), z.number()),
  modules: levels,
  tenantCount: z.number(),
})

export type PlanSummary = z.infer<typeof planSummarySchema>

export const planDetailSchema = planSummarySchema.extend({
  tenants: z.array(
    z.object({
      id: z.uuid(),
      slug: z.string(),
      name: z.string(),
      status: z.string(),
      overrides: z.number(),
    }),
  ),
})

export type PlanDetail = z.infer<typeof planDetailSchema>

const rolloutSchema = z.object({
  tenants: z.array(
    z.object({
      id: z.uuid(),
      slug: z.string(),
      name: z.string(),
      changes: z.array(
        z.object({
          moduleKey: z.string(),
          from: z.enum(ACCESS_LEVELS),
          to: z.enum(ACCESS_LEVELS),
        }),
      ),
      blockers: z.array(z.object({ moduleKey: z.string(), code: z.string(), message: z.string() })),
    }),
  ),
})

export type RolloutPreview = z.infer<typeof rolloutSchema>

export function fetchPlans(signal?: AbortSignal): Promise<{ items: PlanSummary[] }> {
  return api.get('/v1/platform/plans', {
    schema: z.object({ items: z.array(planSummarySchema) }),
    ...(signal ? { signal } : {}),
  })
}

export function fetchPlan(planId: string, signal?: AbortSignal): Promise<PlanDetail> {
  return api.get(`/v1/platform/plans/${planId}`, {
    schema: planDetailSchema,
    ...(signal ? { signal } : {}),
  })
}

/** Quotas as sent: a count, or `null` for unlimited. */
export type QuotaInput = Record<string, number | null>

export function createPlan(input: {
  key: string
  name: string
  description: string | null
  quotas: QuotaInput
  modules: Record<string, AccessLevel>
}): Promise<PlanDetail> {
  return api.post('/v1/platform/plans', { schema: planDetailSchema, body: input })
}

export function updatePlan(
  planId: string,
  input: {
    name?: string
    description?: string | null
    isActive?: boolean
    quotas?: QuotaInput
    modules?: Record<string, AccessLevel>
    force?: boolean
    note?: string
  },
) {
  return api.patch(`/v1/platform/plans/${planId}`, {
    schema: z.object({
      plan: planDetailSchema,
      rollout: z.object({
        applied: z.array(z.string()),
        failed: z.array(z.object({ slug: z.string(), message: z.string() })),
      }),
    }),
    body: input,
  })
}

export function previewPlan(
  planId: string,
  modules: Record<string, AccessLevel>,
): Promise<RolloutPreview> {
  return api.post(`/v1/platform/plans/${planId}/preview`, {
    schema: rolloutSchema,
    body: { modules },
  })
}

export function deletePlan(planId: string) {
  return api.delete(`/v1/platform/plans/${planId}`, { schema: z.object({ ok: z.literal(true) }) })
}

const planChangeSchema = z.object({
  changes: z.array(
    z.object({ moduleKey: z.string(), from: z.enum(ACCESS_LEVELS), to: z.enum(ACCESS_LEVELS) }),
  ),
})

export type PlanChangeInput = {
  planId: string
  keepOverrides: boolean
  force?: boolean
  note?: string
}

export function previewTenantPlan(tenantId: string, input: PlanChangeInput) {
  return api.post(`/v1/platform/tenants/${tenantId}/plan/preview`, {
    schema: planChangeSchema.extend({
      blockers: z.array(z.object({ moduleKey: z.string(), code: z.string(), message: z.string() })),
    }),
    body: input,
  })
}

export function changeTenantPlan(tenantId: string, input: PlanChangeInput) {
  return api.put(`/v1/platform/tenants/${tenantId}/plan`, {
    schema: planChangeSchema.extend({ planId: z.uuid() }),
    body: input,
  })
}
